<template>
  <div class="container">
    <div class="subheader">
      <div class="title" v-if="section">{{ sectionTitle }}</div>
      <div class="buttons v-center">
        <div class="tags-select">
          <v-select
            v-if="section && questionTagsAvailable"
            placeholder="Tags Filter"
            :options="questionTagsAvailable"
            label="label"
            v-model="tagsSelected"
            :multiple="true"
            :clearable="true"
          />
        </div>
        <button
          type="button"
          class="btn btn-invisible-grey"
          @click="revertOrder"
          v-if="changed"
          title="Volgorde herstellen"
          :disabled="isSaving"
        >
          herstellen
        </button>
        <button
          type="button"
          class="btn btn-dark"
          @click="saveOrder"
          v-if="changed"
          :disabled="isSaving"
        >
          <font-awesome-icon v-if="isSaving" icon="spinner" />
          Volgorde opslaan
        </button>
      </div>
    </div>
    <div class="scroller">
      <div
        class="questions cards"
        :class="{ dragging: dragging, enabled: enabled }"
        v-if="initialized"
      >
        <draggable
          v-model="questions"
          @start="startDrag"
          @end="endDrag"
          :forceFallback="true"
          animation="300"
          ghostClass="ghost"
          delay="50"
          touchStartThreshold="30"
          :disabled="!enabled"
        >
          <transition-group
            type="transition"
            :name="dragging ? '' : 'draglist'"
          >
            <!-- draggable=false disables the default browser link dragging
                 behavior, which messes withour sort. -->
            <router-link
              draggable="false"
              class="question card"
              :to="{
                name: 'questions',
                params: {
                  versionId: version.id,
                  sectionId: question.section.id,
                  questionId: question.id,
                },
                query: {
                  ...$route.query,
                },
              }"
              :key="question.id"
              v-for="question in questions"
              :class="{ active: isActive(question) }"
            >
              <div class="row">
                <div
                  class="text"
                  :title="getLanguageValue(question, 'text')"
                  :class="{ fallback: !hasLanguageValue(question, 'text') }"
                >
                  <span class="identifier">#V{{ question.id }}</span> -
                  {{ getLanguageValue(question, 'text') }}
                </div>
                <div
                  class="changed"
                  :title="
                    'Laatst gewijzigd: ' + formatDateTime(question.updated_at)
                  "
                >
                  {{ fromNow(question.updated_at) }}
                </div>
              </div>
              <div class="row question-details">
                <div class="options" :title="joinedOptions(question, '\n')">
                  {{ joinedOptions(question) }}
                </div>
                <div class="summary">
                  <template v-if="question.condition_option">
                    <font-awesome-icon
                      @click.stop.prevent="
                        open(question.condition_option.question)
                      "
                      class="icon rotate-180"
                      :title="
                        'Dit is een vervolgvraag op #V' +
                        question.condition_option.question.id
                      "
                      icon="reply"
                      size="1x"
                    />
                    <span
                      @click.stop.prevent="
                        open(question.condition_option.question)
                      "
                      class="identifier foreign"
                      :title="
                        'Dit is een vervolgvraag op #V' +
                        question.condition_option.question.id
                      "
                      >#V{{ question.condition_option.question.id }}</span
                    >
                  </template>
                  <template
                    v-if="
                      question.condition_option_2 &&
                      (!question.condition_option ||
                        question.condition_option.question.id !==
                          question.condition_option_2.question.id)
                    "
                  >
                    <font-awesome-icon
                      class="icon rotate-180"
                      icon="reply"
                      size="1x"
                      :title="
                        'Dit is een vervolgvraag op #V' +
                        question.condition_option_2.question.id
                      "
                    />
                    <span
                      @click.stop.prevent="
                        open(question.condition_option_2.question)
                      "
                      class="identifier foreign"
                      :title="
                        'Dit is een vervolgvraag op #V' +
                        question.condition_option_2.question.id
                      "
                      >#V{{ question.condition_option_2.question.id }}</span
                    >
                  </template>

                  <font-awesome-icon
                    v-if="question.notes"
                    class="icon"
                    icon="pen"
                    :title="'Notitie: ' + question.notes"
                    size="1x"
                  />
                </div>
                <div class="tags" v-if="questionTagsAvailable">
                  <div
                    :key="'positive-tag-' + tag"
                    v-for="tag in questionTagsAsValues(question.positive_tags)"
                    class="tag positive"
                  >
                    <span>{{ tag }}</span>
                  </div>
                  <div
                    :key="'negative-tag-' + tag"
                    v-for="tag in questionTagsAsValues(question.negative_tags)"
                    class="tag negative"
                  >
                    <span>{{ tag }}</span>
                  </div>
                </div>
              </div>
            </router-link>
          </transition-group>
        </draggable>
        <router-link
          :to="{
            name: 'questions',
            params: {
              versionId: version.id,
              sectionId: sectionId,
              questionId: 0,
            },
          }"
          class="question card new"
          :class="{ active: questionId <= 0 }"
          title="Nieuwe vraag toevoegen"
          v-if="enabled"
        >
          <font-awesome-icon class="icon" icon="plus-circle" size="2x" />
        </router-link>
      </div>
      <div class="center" v-if="!initialized && sectionId">
        <font-awesome-icon icon="spinner" size="2x" />
      </div>
    </div>
  </div>
</template>

<script>
// This component name is a preexisting condition, disable inspection.
/* eslint vue/multi-word-component-names: 0 */
import {
  getQuestionsSection,
  getTagsAvailable,
  saveQuestionsOrder,
} from '@/api';
import moment from 'moment';
import types from '@/config/questionTypes';
import { cloneDeep, isEqual } from 'lodash';
import { getValue, supportedLanguages } from '@/languages';
import promiseStore from '../promiseStore';
import { getCachedQuery, putCachedQuery } from '@/util/query-cache';
import { whileSaving } from '@/util/tools';

const NO_TAGS = {
  label: 'Geen tags',
  value: 'no-tags-selected',
};

const QUERY_CACHE_KEY = 'question-tags';

function restoreQuery(to, next) {
  const cache = getCachedQuery(QUERY_CACHE_KEY);
  if (!to.query.question_tags && cache) {
    const query = {
      ...to.query,
      question_tags: cache,
    };
    next({
      ...to,
      query,
    });
  } else {
    next();
  }
}

export default {
  props: ['sectionId', 'questionId', 'version', 'language'],
  data() {
    return {
      section: null,
      questions: null,
      originalQuestions: null,
      types: types,
      dragging: false,
      questionTagsAvailable: [],
      tagKeys: [],
      isSaving: false,
    };
  },
  created() {
    this.initTagsAvailable();
  },
  computed: {
    initialized() {
      return this.questions !== null;
    },
    changed() {
      return !isEqual(this.questions, this.originalQuestions);
    },
    enabled() {
      // Don't allow sort if tags are selected, because the
      // sort of the remaining questions is undefined.
      return this.version.open && !this.queryTags.length;
    },
    sectionTitle() {
      return this.getLanguageValue(this.section, 'title');
    },
    queryTags() {
      return (this.$route.query.question_tags ?? '')
        .split(',')
        .filter((t) => !!t)
        .sort();
    },

    // The list of selected tags is directly bound to the query, and
    // updates the query directly.
    tagsSelected: {
      get() {
        const keys = this.queryTags;
        return this.questionTagsAvailable.filter((tag) =>
          keys.includes(tag.value)
        );
      },
      set(tags) {
        const keys = tags.map((tag) => tag.value).sort();
        const newQuery = keys.join(',');

        if (newQuery !== this.queryTags.join(',')) {
          putCachedQuery(QUERY_CACHE_KEY, newQuery);

          // Update the route with a new list of tags
          this.$router.push({
            query: { question_tags: newQuery || undefined },
          });
        }
      },
    },
  },
  beforeRouteEnter(to, from, next) {
    restoreQuery(to, next);
  },
  beforeRouteLeave(to, from, next) {
    this.guard(to, from, next);
  },
  beforeRouteUpdate(to, from, next) {
    // Wrap next to be able to restore the query within the route guard
    function newNext() {
      restoreQuery(to, (innerNext) => next(innerNext));
    }

    this.guard(to, from, newNext);
  },
  watch: {
    // Reload the data if the query tags change (and initially)
    queryTags: {
      handler() {
        this.load();
      },
      immediate: true,
    },
  },
  methods: {
    hasLanguageValue(item, key) {
      return !!(item[key] && item[key][this.language]);
    },
    getLanguageValue(item, key) {
      return getValue(supportedLanguages, this.language, item[key]);
    },
    guard(to, from, next) {
      if (
        parseInt(from.params.sectionId) !== parseInt(to.params.sectionId) &&
        this.changed
      ) {
        this.$emit('popup', {
          title: 'Weet je zeker dat je wilt weggaan?',
          content:
            'De wijzigingen in de volgorde zullen niet opgeslagen worden',
          image: 'question',
          confirm: () => {
            this.$emit('popup', null);
            next();
          },
          cancel: () => {
            this.$emit('popup', null);
            next(false);
          },
        });
      } else {
        next();
      }
    },

    filterByTags(questions) {
      const tags = this.tagsSelected;
      if (!tags.length) {
        // If no tags are selected, show all questions. The backend would
        // filter out all questions with any positive tags in this scenario,
        // but that is impractical in the client - you'd no longer be able
        // to see which questions exist unless you know their tags in advance.
        return [...questions];
      }

      // Note: if the explicit "no tags" tag is selected, all questions with
      // a tag will be hidden (because none have that tag). This is functionally
      // equivalent to the normal case of a tenant without tags, so no further
      // functionality needs to be implemented to handle that special case.

      // Return questions by their actual tags filter. That means:
      const selected = this.tagsSelected.map((tag) => tag.value);
      return questions.filter((question) => {
        // - If the question has any positive tags, at least one of them
        //   has to be selected.
        if (
          question.positive_tags.length &&
          !question.positive_tags.some((tag) => selected.includes(tag))
        ) {
          return false;
        }

        // - If the question has any negative tags, none of those may be
        //   selected.
        if (
          question.negative_tags.length &&
          question.negative_tags.some((tag) => selected.includes(tag))
        ) {
          return false;
        }

        return true;
      });
    },
    async initTagsAvailable() {
      const tagsAvailable = await promiseStore.getOrSet(
        'tags-available',
        async () => (await getTagsAvailable()).data
      );

      // Add the option that explicitly defines an empty list to
      // the start of the list.
      this.questionTagsAvailable = [NO_TAGS, ...tagsAvailable];
    },
    async load() {
      if (this.sectionId) {
        this.section = (
          await getQuestionsSection(this.version, this.sectionId)
        ).data;
        this.questions = this.filterByTags(this.section.questions);
        this.originalQuestions = cloneDeep(this.questions);
      }
    },
    open(question) {
      this.$router.push({
        name: 'questions',
        params: {
          versionId: this.version.id,
          sectionId: question.section.id,
          questionId: question.id,
        },
        query: {
          ...this.$route.query,
        },
      });
    },
    isActive(question) {
      return question.id === parseInt(this.questionId);
    },
    truncate(value, length) {
      return value.length > length
        ? value.substr(0, length - 3) + '...'
        : value;
    },
    joinedOptions(question, glue = ', ') {
      return question.options
        .map((o) => this.getLanguageValue(o, 'text'))
        .join(glue);
    },
    questionTagsAsValues(tags) {
      if (!tags || !tags.length) {
        return [];
      }

      return tags.map((tag) => {
        const f = this.questionTagsAvailable.find(
          (tagObj) => tagObj.value === tag
        );

        return f ? f.label : 'Missing:' + tag;
      });
    },
    formatDateTime(value) {
      return moment(value).format('DD-MM-YYYY hh:mm:ss');
    },
    fromNow(datetime) {
      return moment(datetime).fromNow();
    },
    startDrag() {
      this.dragging = true;
    },
    endDrag() {
      this.dragging = false;
    },
    revertOrder() {
      this.questions = cloneDeep(this.originalQuestions);
    },
    async saveOrder() {
      await whileSaving(this, 'isSaving', () =>
        saveQuestionsOrder(this.version, this.section, this.questions)
      );
      this.originalQuestions = cloneDeep(this.questions);
    },
  },
};
</script>

<style scoped lang="scss">
.questions:not(.dragging) {
  .question:hover:not(.active) {
    background-color: $bg-color-light-grey;
  }
}

.container {
  font-family: $font-family-gitlab;
  display: flex;
  flex-direction: column;

  border-right: solid 1px $bg-color-border;

  .scroller {
    overflow-y: auto;

    .draglist-move {
      transition: transform 0.7s;
    }

    .questions.enabled {
      .question {
        /*cursor: grab;*/
        cursor: pointer;
      }
    }

    .questions:not(.enabled) {
      .question {
        cursor: pointer;
      }
    }

    .questions {
      display: flex;
      flex-direction: column;
      background-color: white;

      .question.active {
        outline-style: none;
        background-color: $bg-color-light-blue;
      }

      .question.ghost {
        background-color: $bg-color-light-grey;
        /*opacity: 0.7;*/
      }

      .question.new {
        color: rgba(0, 0, 0, 0.125);
        border: 1px dashed rgba(0, 0, 0, 0.1);
        justify-content: center;
        align-items: center;
      }

      .question {
        background-color: white;
        padding: 5px 15px;
        font-weight: 400;
        font-size: 14px;
        height: 52px;

        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 0.25rem;
        display: flex;
        flex-direction: column;
        text-decoration: none;
        color: black;

        .row {
          width: 100%;
          margin: 5px 0 5px 0;
          display: flex;
          flex-direction: row;

          &.question-details {
            justify-content: space-between;
          }

          .identifier.foreign {
            padding: 0 5px;
            color: grey;
            text-decoration: none;
          }

          .identifier:hover {
            text-decoration: underline;
          }

          .text {
            flex: 2;
          }

          .text.fallback {
            color: grey;
          }

          .options {
            flex: 2;
            font-size: 12px;
            color: grey;
          }

          .changed {
            font-size: 12px;
            color: grey;
            width: 150px;
            text-align: right;
          }

          .text,
          .notes,
          .options,
          .changed {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .summary {
            width: 150px;
            text-align: right;
            color: #9f9f9f;
            font-size: 12px;

            .icon {
              margin: 0 -2px 0 3px;
            }

            .rotate-180 {
              transform: rotate(180deg);
            }
          }
        }
      }
    }
  }
}

.tags-select {
  min-width: 10rem;
}
</style>
